import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logOut, setCredentials } from "../../features/auth/authSlice";
import { ACCESS_TOKEN, API_BASE_URL } from '../../constants';
import { Mutex } from 'async-mutex';

const getRenderer = () => {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  if (gl) {
    return gl.getParameter(gl.RENDERER);
  } else {
    return 'Unknown';
  }
}

const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  console.log("userAgent: ", userAgent)
  let browser, version, os, osVersion;

  // Detect browser
  if (userAgent.indexOf("Chrome") !== -1) {
    browser = "Chrome";
    version = /Chrome\/([0-9]+)/.exec(userAgent)[1];
  } else if (userAgent.indexOf("Safari") !== -1) {
    browser = "Safari";
    version = /Version\/([0-9]+)/.exec(userAgent)[1];
  } else if (userAgent.indexOf("Firefox") !== -1) {
    browser = "Firefox";
    version = /Firefox\/([0-9]+)/.exec(userAgent)[1];
  } else if (userAgent.indexOf("Edge") !== -1) {
    browser = "Edge";
    version = /Edge\/([0-9]+)/.exec(userAgent)[1];
  } else if (userAgent.indexOf("Opera") !== -1 || userAgent.indexOf("OPR") !== -1) {
    browser = "Opera";
    version = /OPR\/([0-9]+)/.exec(userAgent)[1];
  } else {
    browser = "Unknown";
    version = "Unknown";
  }

  // Detect operating system
  if (userAgent.indexOf("Windows") !== -1) {
    os = "Windows";
    osVersion = /Windows NT ([0-9.]+)/.exec(userAgent)[1];
  } else if (userAgent.indexOf("Linux") !== -1) {
    os = "Linux";
    osVersion = "Unknown";
  } else if (userAgent.indexOf("Mac OS X") !== -1) {
    os = userAgent.indexOf("iPhone") !== -1 ? "iOS" : "Mac";
    const macOSMatch = /(?:Mac OS X|CPU iPhone OS) ([0-9_]+)/.exec(userAgent);
    osVersion = macOSMatch ? macOSMatch[1].replace(/_/g, ".") : "Unknown";
  } else {
    os = "Unknown";
    osVersion = "Unknown";
  }

  return { name: browser, version, os, osVersion };
}

const getFingerprint = async () => {
  const browserInfo = getBrowserInfo();

  const fingerprint = {
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    userAgent: navigator.userAgent,
    language: navigator.language || navigator.userLanguage,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform: navigator.userAgentData?.platform || navigator.platform,
    renderer: getRenderer(),
    browser: browserInfo.name,
    browserVersion: browserInfo.version,
    os: browserInfo.os,
    osVersion: browserInfo.osVersion
  };

  return JSON.stringify(fingerprint);
}

const mutex = new Mutex(); // library to do only one request at a time (this will apply only when the token is expired and we need to refresh it, that way we avoid multiple requests to refresh the token)

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: 'include',
  prepareHeaders: async (headers, { getState }) => {
    const token = getState().auth.accessToken || localStorage.getItem(ACCESS_TOKEN)
    if(token){
      headers.set("authorization", `Bearer ${token}`)
    }

    headers.set("X-Custom-Fingerprint", await getFingerprint());
    return headers
  }
})

const baseQueryWithReauth = async ( args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()

  // wait for the original request to complete
  console.log("Executing baseQueryWithReauth")
  let result = await baseQuery(args, api, extraOptions)
  console.log("baseQuery result: ",result)

  // check the result of the original request
  if(result?.error?.status === 403 || result?.error?.status == 401){
    if(!mutex.isLocked()){
      // if not locked,then lock it
      const release = await mutex.acquire()
      
      // if the result was unauthorized and we are not already refreshing the token , try to refresh the token
      
      try {
        // send refresh token to get new access token
        console.log('sending refresh token')
        const refreshResult = await baseQuery({url: 'Accounts/refresh-token', method: 'POST'}, api, extraOptions)
        console.log("refresh result: ",JSON.stringify(refreshResult))

        if(refreshResult?.data){
          // store the new token
          let updatedAuth = { accessToken: refreshResult.data.jwtToken , user: refreshResult.data }
          console.log("refresh token success, storing new token: ", updatedAuth)
          api.dispatch(setCredentials(updatedAuth))
          // Update local storage with new access token
          localStorage.setItem(ACCESS_TOKEN, refreshResult.data.jwtToken)
          // retry the original query with new access token and overwrite the previous result
          result = await baseQuery(args, api, extraOptions);
        } else if(result?.error?.status === 403 || result?.error?.status == 401 || result?.error?.status == 400) {
          api.dispatch(logOut())
          let current = window.location.pathname;
          console.log("Failed to refresh token, redirecting to login page")
          current.includes("dashboard") && window.location.replace("/");
        }

      } finally {
        // release must be called once the mutex should be released again.
        // release the mutex
        release()
      }

    } else {
      console.log("baseQueryWithReauth: mutex is locked, waiting for unlock")
      await mutex.waitForUnlock()
      console.log("baseQueryWithReauth: mutex is unlocked, retrying original query: " + args.url)
      result = await baseQuery(args, api, extraOptions)
      if(result?.error?.status === 403 || result?.error?.status == 401){
        api.dispatch(logOut())
        let current = window.location.pathname;
        console.log("Unauthorized, redirecting to login page")
        current.includes("dashboard") && window.location.replace("/");
      }
    }
  }

  // return the result of the original query or if it was unauthorized, the result of the retry
  console.log("returning final result: ", JSON.stringify(result), " for url: ", args.url, " and method: ", args.method || "GET")  
  return result
}

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // we are going to use injected endpoints
  })
})